export const useOrganisation = defineStore('useOrganisation', () => {
	const uAuth = useAuth()
	const { user } = storeToRefs(uAuth)
	const organisation: Ref<OrganisationWithAllInformation | undefined> = ref()

	// Gets the organisation for the authenticated user from the api
	async function getOrganisation() {
		// Fetch the organisation
		try {
			organisation.value = await $fetch<OrganisationWithAllInformation>('/api/auth/organisations', {
				method: 'GET',
				headers: useRequestHeaders(['cookie']),
				credentials: 'include',
				onResponseError({ response }) {
					throw response._data
				},
			})
		} catch (error) {
			organisation.value = undefined

			// If the error is 404, then the user is not part of an organisation -> ignore the error
			if ((error as RestErr).status === 404) {
				return
			}

			throw error
		}
	}

	// Computes the departments from the organisation
	const departments = computed(() => {
		return organisation.value?.departments ?? []
	})

	// Computes the users from the organisation without the authenticated user
	const users = computed(() => {
		// return (
		// 	organisation.value?.users?.filter(
		// 		(organisationUser) => organisationUser.id !== user.value?.id
		// 	) ?? []
		// )
		return organisation.value?.users ?? []
	})

	// Gets the department with the given id
	function getDepartmentById(id: number) {
		return organisation.value?.departments?.find((department) => department.id === id)
	}

	// Gets the users of the department with the given id
	function getUsersByDepartmentId(id: number) {
		return organisation.value?.users?.filter((user) => user.department?.id === id) ?? []
	}

	// Gets the user that is the leader of the department with the given id
	function getLeaderUserIdByDepartmentId(id: number) {
		return organisation.value?.departments?.find((department) => department.id === id)?.leaderId
	}

	// Gets all users that can be assigned to a task
	function getSelectableUsers(canAssignToMe: boolean, departmentId?: number): UserSelectOption[] {
		let selectableUsers: User[] = []
		if (departmentId) {
			selectableUsers = getUsersByDepartmentId(departmentId)
		} else {
			// Selection is not limited to a department
			selectableUsers = users.value
		}

		// If it can be assigned to me, bring myself to the top of the list
		if (canAssignToMe) {
			const currentUser = selectableUsers.find((listUser) => listUser.id === user.value?.id)
			if (currentUser) {
				selectableUsers = [
					currentUser,
					...selectableUsers.filter((user) => user.id !== currentUser.id),
				]
			}
		}
		// // Filter out the current user if canAssignToMe is true
		// if (canAssignToMe) {
		// 	selectableUsers = selectableUsers.filter((listUser) => listUser.id !== user.value?.id)
		// }

		// Return the SelectUserOption array
		return selectableUsers.map((user) => {
			return getSelectableUserFromUser(user)
		})
	}

	// Gets one selectable user
	function getSelectableUser(userId: number): UserSelectOption | undefined {
		const user = users.value.find((user) => user.id === userId)
		if (user) {
			return getSelectableUserFromUser(user)
		}
		return undefined
	}

	// Gets the selectable user from a normal user
	function getSelectableUserFromUser(user: User): UserSelectOption {
		const userLabel = `${user.firstName} ${user.lastName}`
		const avatarSource = user.avatarKey
			? useRuntimeConfig().public.apiBasePath + `/auth/avatars/${user.avatarKey}`
			: userLabel
		return {
			value: user.id,
			label: userLabel,
			avatar: {
				src: avatarSource,
				alt: userLabel,
			},
		}
	}

	// Update the department with the given id
	function updateDepartmentWithId(id: number, department: DepartmentWithUsers) {
		const index = organisation.value?.departments?.findIndex((department) => department.id === id)
		if (index !== undefined && index !== -1 && organisation.value?.departments) {
			organisation.value.departments[index] = department
		} else {
			throw useRestError().newNotFoundError(
				'Abteilung wurde nicht gefunden und konnte deswegen nicht aktualisiert werden.'
			)
		}
	}

	// Removes a department
	function removeDepartmentWithId(id: number) {
		const index = organisation.value?.departments?.findIndex((department) => department.id === id)
		if (index !== undefined && index !== -1) {
			organisation.value?.departments?.splice(index, 1)
		} else {
			throw useRestError().newNotFoundError(
				'Abteilung wurde nicht gefunden und konnte deswegen nicht entfernt werden.'
			)
		}
	}

	// Removes the leadership from the current department leader user with the given department id
	function removeLeadershipFromCurrentLeader(departmentId: number) {
		const leaderId = getLeaderUserIdByDepartmentId(departmentId)
		if (leaderId) {
			const leader = getUserById(leaderId)
			if (leader) {
				leader.isDepartmentLeader = false
				updateUserWithId(leaderId, leader)
			}
		}
	}

	// Add a user to a department
	function addUserToDepartment(departmentId: number, userId: number) {
		const department = getDepartmentById(departmentId)
		if (department) {
			department.userIds.push(userId)
			updateDepartmentWithId(departmentId, department)
		}
	}

	// Remove a user from a department
	function removeUserFromDepartment(departmentId: number, userId: number) {
		const department = getDepartmentById(departmentId)
		if (department) {
			const index = department.userIds.findIndex((id) => id === userId)
			if (index !== undefined && index !== -1) {
				department.userIds.splice(index, 1)
				updateDepartmentWithId(departmentId, department)
			}
		}
	}

	// Gets the user with the given id
	function getUserById(id: number) {
		return organisation.value?.users?.find((user) => user.id === id)
	}

	// Updates the user with the given id
	function updateUserWithId(id: number, user: User) {
		const index = organisation.value?.users?.findIndex((user) => user.id === id)
		if (index !== undefined && index !== -1 && organisation.value?.users) {
			organisation.value.users[index] = user
		} else {
			throw useRestError().newNotFoundError(
				'Benutzer wurde nicht gefunden und konnte deswegen nicht aktualisiert werden.'
			)
		}
	}

	// removes a user with the given id
	function removeUserWithId(id: number) {
		const index = organisation.value?.users?.findIndex((user) => user.id === id)
		if (index !== undefined && index !== -1) {
			organisation.value?.users?.splice(index, 1)
		} else {
			throw useRestError().newNotFoundError(
				'Benutzer wurde nicht gefunden und konnte deswegen nicht entfernt werden.'
			)
		}
	}

	async function logout() {
		// Logout the user
		await uAuth.logoutUser()

		// Clear the organisation data
		organisation.value = undefined
	}

	return {
		organisation,
		departments,
		users,
		getOrganisation,
		getDepartmentById,
		// loadDepartmentById,
		removeDepartmentWithId,
		getUsersByDepartmentId,
		getLeaderUserIdByDepartmentId,
		getSelectableUsers,
		getSelectableUser,
		getSelectableUserFromUser,
		getUserById,
		updateUserWithId,
		removeUserWithId,
		updateDepartmentWithId,
		removeLeadershipFromCurrentLeader,
		addUserToDepartment,
		removeUserFromDepartment,
		logout,
	}
})
